import React from "react";
import { useI18n } from "../../../hooks/useI18n";
import FormFieldGroup from "../FormFieldGroup";
import "../EngagementMaster.scss";
import { getServiceDetailsFormFields } from "./ServiceDetailsFormFields";
import { getAOAServiceDetailsFormFields } from "./AOAServiceDetailsFormFields";
import { useSelector } from "react-redux";
import CommentsSection from "../../CommentsSection/CommentsSection";
import {
  isEngagementManager as checkIsEngagementManager,
  isResourceManager as checkIsResourceManager,
} from "../../../util/roleHelpers";

function formatDelegates(delegates) {
  if (!Array.isArray(delegates) || delegates.length === 0) return "";
  return delegates
    .map((d) => d.delegateName || d.delegateEmail || d.emailAddress)
    .filter(Boolean)
    .join(", ");
}

function formatDelegateEmails(delegates) {
  if (!Array.isArray(delegates) || delegates.length === 0) return "";
  return delegates
    .map((d) => d.delegateEmail || d.emailAddress)
    .filter(Boolean)
    .join(", ");
}

function formatDisplayDate(value) {
  if (!value) return "";
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) return `${match[3]}/${match[2]}/${match[1]}`;
  return value;
}

const ServiceDetailsTab = ({ data = {}, domain, currentStageId, isTerminal = false, isDisabled: isDisabledProp, viewContext }) => {
  const isDisabled = isDisabledProp ?? isTerminal;
  const { t } = useI18n();
  const roleData = useSelector((state) => state.landing.roleData);
  const userIsEngagementManager = checkIsEngagementManager(roleData?.role);
  const userIsResourceManager = checkIsResourceManager(roleData?.role);

  // Prefer viewContext passed from the parent (ViewDetailsPage -> EngagementMaster).
  // Fall back to role-based logic when the prop isn't provided.
  const resolvedViewContext =
    viewContext ??
    (userIsResourceManager
      ? "resource"
      : userIsEngagementManager
        ? "engagement"
        : null);
  const isEngagementManager = resolvedViewContext === "engagement";
  const isResourceManager = resolvedViewContext === "resource";

  const isAOA = String(domain).toLowerCase().includes("aoa");

  const emOnlyFields = ["revisedStartDate", "revisedEndDate", "delegates"];
  const getFields = isAOA ? getAOAServiceDetailsFormFields : getServiceDetailsFormFields;
  const formFields = getFields(t, data).map((fieldRow) => ({
    ...fieldRow,
    row: fieldRow.row.filter(
      (field) =>
        isEngagementManager || !emOnlyFields.includes(field.name || field.type),
    ),
  }));
  const sectionBreakFieldCounts = isAOA ? [10, 12] : [12, 14];
  let cumulativeFieldCount = 0;

  return (
    <div className="service-details-tab">
      <div className="form-container">
        {formFields.map((fieldRow, index) => {
          cumulativeFieldCount += fieldRow.row.length;
          const isSectionBreak =
            sectionBreakFieldCounts.includes(cumulativeFieldCount);
          return (
            <div
              key={index}
              className={`form-row ${isSectionBreak ? "section-break" : ""}`}
            >
              {fieldRow.row.map((field, fieldIndex) => {
                let displayValue = field.value;
                let tooltip = field.tooltip;
                if (field.type === "delegates") {
                  displayValue = formatDelegates(field.value);
                  tooltip = formatDelegateEmails(field.value);
                } else if (field.type === "date") {
                  displayValue = formatDisplayDate(field.value);
                }
                return (
                  <FormFieldGroup
                    key={fieldIndex}
                    label={field.label}
                    value={displayValue}
                    gridClass={field.gridClass}
                    tooltip={tooltip}
                  />
                );
              })}
            </div>
          );
        })}
      </div>

      <CommentsSection
        comments={data.comments}
        requestId={data.requestId}
        domain={domain}
        readOnly={isDisabled || isResourceManager}
      />
    </div>
  );
};

ServiceDetailsTab.displayName = "ServiceDetailsTab";
 
export default ServiceDetailsTab;